import { useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, Grid, MenuItem, Paper, Stack, TextField, Typography, useTheme } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { tokens } from "../../../../../theme";
import type { AxiosResponse } from "axios";
import apiService from "../../../../../service/Api";
import { BookingData } from "../../../types/view-booking";
import { SEOConfigApiItem, SEOConfigsApiResponse } from "../../../../configurations/seo-management/services/seoConfig";
import { SiteListApiItem, SitesApiResponse } from "../../../../configurations/site-listings/services/siteListingConfig";

type Draft = { title: string; description: string; keywords: string; focus_keyword: string; og_image_url: string; handle: string };
type Defaults = { values: Draft; origin: string; warning: string };

const SEO_VARIABLES = [
  ["deceased_name", "Deceased name"], ["stream_title", "Stream title"],
  ["deceased_first_name", "First name"], ["deceased_second_name", "Second name"],
  ["deceased_surname", "Surname"], ["date_of_birth", "Date of birth"],
  ["date_of_death", "Date of death"],
  ["deceased_image", "Deceased image"],
  ["stream_date", "Stream date"], ["service_type", "Service type"],
  ["location", "Service location"], ["city", "City"],
  ["venue_name", "Venue name"], ["booking_id", "Booking ID"],
  ["booking_reference", "Booking reference"],
] as const;

function makeSEOContext(booking: BookingData, stream: BookingData["streams"][number]): Record<string, string> {
  const dateValue = stream.date || "";
  const date = dateValue ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(dateValue) ? `${dateValue}T12:00:00` : dateValue) : null;
  const deceasedName = booking.beloved?.name || stream.title || "";
  const deceasedParts = deceasedName.split(/\s+/);
  const venueName = stream.venueDetails?.name || "";
  return {
    deceased_name: deceasedName,
    deceased_first_name: deceasedParts[0] || "",
    deceased_second_name: booking.beloved?.details?.second_name || "",
    deceased_surname: deceasedParts.length > 1 ? deceasedParts[deceasedParts.length - 1] : "",
    date_of_birth: booking.beloved?.details?.dateOfBirth || "",
    date_of_death: booking.beloved?.details?.dateOfDeath || "",
    deceased_image: booking.beloved?.avatar && booking.beloved.avatar !== "/default-avatar.jpg" ? booking.beloved.avatar : "",
    stream_title: stream.title || "",
    stream_date: date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : stream.date || "",
    service_type: booking.service || "",
    location: [venueName, stream.city].filter(Boolean).join(", "),
    city: stream.city || "",
    venue_name: venueName,
    booking_id: String(booking.id),
    booking_reference: booking.reference_number ? String(booking.reference_number) : "",
  };
}

function interpolate(template: string, context: Record<string, string>) {
  return template.replace(/\{\{(\w+)\}\}/g, (_token, key: string) => context[key] ?? "");
}

function streamURL(link?: string): URL | null {
  try {
    const url = new URL(link || "");
    return ["https:", "http:"].includes(url.protocol) ? url : null;
  } catch {
    return null;
  }
}

function host(domain: string): string {
  return streamURL(domain.includes("://") ? domain : `https://${domain}`)?.hostname.toLowerCase().replace(/^www\./, "") || "";
}

async function loadSites(): Promise<SiteListApiItem[]> {
  const sites: SiteListApiItem[] = [];
  let next: string | null = "/api/configurations/sites/?page_size=1000";
  while (next) {
    const response: AxiosResponse<SitesApiResponse> = await apiService.get<SitesApiResponse>(next);
    sites.push(...response.data.results);
    // Keep subsequent requests on the configured API origin.
    const nextURL: URL | null = response.data.next ? new URL(response.data.next, "https://api.local") : null;
    next = nextURL ? `${nextURL.pathname}${nextURL.search}` : null;
  }
  return sites;
}

export default function BookingSEO({ bookingData, active }: { bookingData: BookingData; active: boolean }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selected, setSelected] = useState(0);
  const [defaults, setDefaults] = useState<Defaults[]>([]);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [seoActive, setSeoActive] = useState(Boolean(bookingData.seo_active));
  const [savedSEOActive, setSavedSEOActive] = useState(Boolean(bookingData.seo_active));
  const [savedMetadata, setSavedMetadata] = useState<Record<string, string>>(bookingData.seo_metadata || {});
  const [saving, setSaving] = useState(false);
  const streams = bookingData.streams;

  useEffect(() => {
    if (!active || loaded || !streams.length) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    async function load() {
      try {
        const sites = await loadSites();
        const configs = new Map<number, SEOConfigApiItem | undefined>();
        const values = await Promise.all(streams.map(async (stream): Promise<Defaults> => {
          const url = streamURL(stream.website_stream_link);
          const streamHost = url ? host(url.origin) : "";
          const site = sites.find((item) => host(item.domain) === streamHost);
          let config: SEOConfigApiItem | undefined;
          if (site) {
            if (!configs.has(site.id)) {
              const response = await apiService.get<SEOConfigsApiResponse>(
                "/api/configurations/seo-configs/",
                { site: site.id, page_name: "stream_page", is_active: true, page_size: 1000 },
              );
              configs.set(site.id, response.data.results[0]);
            }
            config = configs.get(site.id);
          }
          const context = makeSEOContext(bookingData, stream);
          const saved = savedMetadata;
          return {
            values: {
              title: seoActive && saved.title ? saved.title : config?.meta_title_template || stream.title,
              description: seoActive && saved.description ? saved.description : config?.meta_description_template || "",
              keywords: seoActive && saved.keywords ? saved.keywords : config?.meta_keywords || "",
              focus_keyword: seoActive && saved.focus_keyword ? saved.focus_keyword : config?.focus_keyword || "",
              og_image_url: seoActive && saved.og_image_url ? saved.og_image_url : config?.og_image_url || "",
              handle: url?.pathname.replace(/^\//, "") || "",
            },
            origin: url?.origin || "",
            warning: !url ? "This stream has no website URL yet. A URL preview will be available once it is configured."
              : !site ? "No matching website configuration was found for this stream."
              : !config ? "No active stream-page SEO defaults were found for this website." : "",
          };
        }));
        if (!cancelled) {
          setDefaults(values);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setError("Could not load the website SEO defaults. Retry to load the configured values.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [active, loaded, streams, bookingData, savedMetadata, seoActive, attempt]);

  if (!streams.length) return <Alert severity="info">SEO settings will be available once a stream is added to this booking.</Alert>;
  if (loading) return <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 5 }}><CircularProgress size={24} /><Typography>Loading stream SEO defaults…</Typography></Stack>;
  if (error) return <Alert severity="warning" action={<Button color="inherit" onClick={() => setAttempt((value) => value + 1)}>Retry</Button>}>{error}</Alert>;
  const current = defaults[selected];
  if (!current) return null;
  const draft = drafts[selected] || current.values;
  const changed = Object.keys(current.values).some((key) => draft[key as keyof Draft] !== current.values[key as keyof Draft]) || seoActive !== savedSEOActive;
  const update = (key: keyof Draft, value: string) => setDrafts((previous) => ({ ...previous, [selected]: { ...draft, [key]: value } }));
  const handle = draft.handle.replace(/^\/+/, "");
  const fullURL = current.origin ? `${current.origin}/${handle}` : "";
  const context = makeSEOContext(bookingData, streams[selected]);
  const saveSEO = async () => {
    setSaving(true);
    try {
      const metadata = {
        title: draft.title,
        description: draft.description,
        keywords: draft.keywords,
        focus_keyword: draft.focus_keyword,
        og_image_url: draft.og_image_url,
      };
      await apiService.patch(`/api/bookings/booking/${bookingData.id}/`, {
        seo_active: seoActive,
        seo_metadata: metadata,
      });
      setSavedSEOActive(seoActive);
      setSavedMetadata(metadata);
      setDrafts({});
      setLoaded(false);
      setAttempt((value) => value + 1);
    } catch {
      setError("Could not save the booking SEO settings. Please retry.");
    } finally {
      setSaving(false);
    }
  };
  const autoFillRecommended = () => {
    const titleTemplates = [
      "{{deceased_name}} - Memorial & Funeral Stream",
      "Remembering {{deceased_name}} | {{stream_date}}",
      "{{deceased_name}} Memorial Service in {{city}}",
      "Watch {{deceased_name}}'s {{service_type}} Service Online",
      "{{stream_title}} | {{venue_name}} Memorial Stream",
    ];
    const descriptionTemplates = [
      "Watch the live memorial stream for {{deceased_name}} on {{stream_date}}. Join family and friends to share memories and condolences.",
      "Join us online for {{deceased_name}}'s {{service_type}} service in {{city}} on {{stream_date}}. View the stream and leave a tribute.",
      "Remember {{deceased_name}} with family and friends at {{venue_name}}. Watch the memorial stream and share your condolences online.",
      "Celebrate the life of {{deceased_name}}. Watch {{stream_title}} live from {{location}} on {{stream_date}}.",
      "View the funeral stream for {{deceased_first_name}} {{deceased_surname}} on {{stream_date}}. Send a tribute and support the family online.",
    ];
    const keywordTemplates = [
      "{{deceased_name}}, memorial stream, funeral service, livestream",
      "{{deceased_name}} memorial, {{city}} funeral stream, online tribute",
      "{{deceased_name}}, {{service_type}} service, live funeral stream",
      "{{deceased_name}} funeral, {{venue_name}}, memorial service online",
    ];
    const focusKeywordTemplates = [
      "{{deceased_name}} memorial stream",
      "{{deceased_name}} funeral service",
      "{{deceased_name}} livestream",
      "{{city}} memorial stream for {{deceased_name}}",
    ];
    const randomItem = <T,>(items: T[]) => items[Math.floor(Math.random() * items.length)];
    setSeoActive(true);
    setDrafts((previous) => ({
      ...previous,
      [selected]: {
        ...draft,
        title: randomItem(titleTemplates),
        description: randomItem(descriptionTemplates),
        keywords: randomItem(keywordTemplates),
        focus_keyword: randomItem(focusKeywordTemplates),
        og_image_url: "{{deceased_image}}",
      },
    }));
  };

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      <Stack direction="row" justifyContent="flex-end" spacing={1.25} sx={{ mb: 1.5, px: { xs: 1, sm: 0 } }}>
        <Button
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          disabled={saving}
          onClick={autoFillRecommended}
          sx={{ textTransform: "uppercase", borderRadius: 2, color: colors.primary[400], borderColor: colors.primary[400], fontWeight: 600 }}
        >
          Auto-fill recommended
        </Button>
        <Button variant="contained" disabled={!changed || saving} onClick={saveSEO} sx={{ textTransform: "uppercase" }}>
          {saving ? "Saving…" : "Save SEO"}
        </Button>
      </Stack>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 8 }} sx={{ minWidth: 0 }}>
          <Stack spacing={2}>
            <Paper elevation={0} sx={{ borderRadius: "12px", p: { xs: 2, sm: 3 } }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ display: "flex", p: 1.25, borderRadius: "10px", bgcolor: colors.primary[900], color: colors.primary[400] }}>
                    <TuneOutlinedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Stream SEO</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Manage how this stream appears in search results.</Typography>
                  </Box>
                </Stack>
                {streams.length > 1 && (
                  <TextField select label="Stream" size="small" value={selected} onChange={(event) => setSelected(Number(event.target.value))} sx={{ minWidth: 200, maxWidth: { sm: 300 } }}>
                    {streams.map((stream, index) => <MenuItem key={stream.id ?? index} value={index}>{stream.title || `Stream ${index + 1}`}</MenuItem>)}
                  </TextField>
                )}
              </Stack>
            </Paper>
            {current.warning && <Alert severity="warning" sx={{ borderRadius: "12px" }}>{current.warning}</Alert>}
            <Paper elevation={0} sx={{ borderRadius: "12px", overflow: "hidden" }}>
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Search engine details</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>Start with your website defaults and tailor the details for this stream.</Typography>
              </Box>
              <Divider />
              <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 }, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                <Box><TextField fullWidth label="Page title template" value={draft.title} onChange={(event) => update("title", event.target.value)} error={draft.title.length > 70} helperText={`${interpolate(draft.title, context).length} of 70 recommended characters in preview`} />{SEO_VARIABLES.map(([key, label]) => <Button key={key} size="small" onClick={() => update("title", `${draft.title}{{${key}}}`)} sx={{ textTransform: "none", minWidth: 0, mr: 0.5 }}>{label}</Button>)}</Box>
                <Box><TextField fullWidth multiline minRows={4} label="Meta description template" value={draft.description} onChange={(event) => update("description", event.target.value)} error={interpolate(draft.description, context).length > 160} helperText={`${interpolate(draft.description, context).length} of 160 recommended characters in preview`} />{SEO_VARIABLES.map(([key, label]) => <Button key={key} size="small" onClick={() => update("description", `${draft.description}{{${key}}}`)} sx={{ textTransform: "none", minWidth: 0, mr: 0.5 }}>{label}</Button>)}</Box>
                <Box>
                  <TextField fullWidth label="Open Graph image URL" value={draft.og_image_url} onChange={(event) => update("og_image_url", event.target.value)} helperText="Paste a public image URL or insert the deceased image variable." />
                  {SEO_VARIABLES.filter(([key]) => key === "deceased_image").map(([key, label]) => <Button key={key} size="small" onClick={() => update("og_image_url", `${draft.og_image_url}{{${key}}}`)} sx={{ textTransform: "none", minWidth: 0, mr: 0.5 }}>{label}</Button>)}
                </Box>
                <TextField fullWidth label="Meta keywords template" value={draft.keywords} onChange={(event) => update("keywords", event.target.value)} helperText={`Preview: ${interpolate(draft.keywords, context)}`} />
                <TextField fullWidth label="Focused keyword template" value={draft.focus_keyword} onChange={(event) => update("focus_keyword", event.target.value)} helperText={`Preview: ${interpolate(draft.focus_keyword, context)}`} />
                <FormControlLabel control={<Checkbox checked={seoActive} onChange={(event) => setSeoActive(event.target.checked)} />} label="Use this SEO customization for this booking" />
                <TextField fullWidth label="URL handle" placeholder="memorial-page/stream-name" value={draft.handle} onChange={(event) => update("handle", event.target.value)} helperText={fullURL || "No website URL configured for this stream"} slotProps={{ formHelperText: { sx: { overflowWrap: "anywhere" } } }} />
              </Stack>
              <Divider />
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-start" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5} sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: changed ? "warning.main" : colors.primary[400] }} />
                  <Typography variant="body2" color="text.secondary">{changed ? "Unsaved preview changes" : "Using stream defaults"}</Typography>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }} component="aside" aria-label="SEO preview" sx={{ minWidth: 0, position: { md: "sticky" }, top: { md: 24 } }}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <OpenInNew fontSize="small" color="action" />
                <Typography fontWeight={800}>Search Previews</Typography>
              </Box>
              <Stack spacing={2.5} aria-live="polite" sx={{ overflowWrap: "anywhere" }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Google Search</Typography>
                  <Typography sx={{ color: theme.palette.mode === "dark" ? "#8ab4f8" : "#1a0dab", fontSize: 18, lineHeight: 1.3, mt: 0.5 }}>{interpolate(draft.title, context) || "Untitled page"}</Typography>
                  <Typography variant="body2" sx={{ color: "#006621" }}>{fullURL || "Stream URL not configured"}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{(interpolate(draft.description, context) || "No description set.").slice(0, 160)}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">Facebook / Open Graph</Typography>
                  {interpolate(draft.og_image_url, context) && <Box component="img" src={interpolate(draft.og_image_url, context)} alt="Open Graph preview" sx={{ display: "block", width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 1, mt: 0.5, mb: 0.5 }} onError={(event) => { (event.target as HTMLImageElement).style.display = "none"; }} />}
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>{fullURL || "Stream URL not configured"}</Typography>
                  <Typography fontWeight={700} variant="body2">{interpolate(draft.title, context) || "Untitled page"}</Typography>
                  <Typography variant="body2" color="text.secondary">{interpolate(draft.description, context) || "No description set."}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">Twitter / X</Typography>
                  {interpolate(draft.og_image_url, context) && <Box component="img" src={interpolate(draft.og_image_url, context)} alt="Social preview" sx={{ display: "block", width: "100%", maxHeight: 120, objectFit: "cover", borderRadius: 1, mt: 0.5, mb: 0.5 }} onError={(event) => { (event.target as HTMLImageElement).style.display = "none"; }} />}
                  <Typography fontWeight={700} variant="body2" sx={{ mt: 0.5 }}>{interpolate(draft.title, context) || "Untitled page"}</Typography>
                  <Typography variant="body2" color="text.secondary">{interpolate(draft.description, context) || "No description set."}</Typography>
                  <Typography variant="caption" color="text.secondary">{fullURL || "Stream URL not configured"}</Typography>
                </Box>
              </Stack>
            </Paper>
            <Paper elevation={0} sx={{ borderRadius: "12px", p: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Preview only</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{seoActive ? `Active customization. Preview values: ${interpolate(draft.keywords, context) || "no keywords"}; focused keyword: ${interpolate(draft.focus_keyword, context) || "not set"}.` : "Customization is inactive; existing stream-page defaults remain in use."}</Typography>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
